import { lazy, Suspense, useMemo, useState } from "react";
import { fotoScript } from "@jomcerdik/shared";
import { useSains } from "./sainsLang";
import { t } from "../../../lib/i18n";
import { Card, Screen, StudyGate, TopBar } from "../kit";
import { GHOST, ListenButton, MiniQuiz, QUIZ_ROUNDS, webglOk, type QuizItem } from "./scienceKit";

// three.js is only downloaded when the 3D game is opened
const Fotosintesis3D = lazy(() => import("./Fotosintesis3D"));
const FOTO_DETAILS = { game: "sains-fotosintesis" };

export function FotosintesisPage() {
  return <Screen><StudyGate><Fotosintesis /></StudyGate></Screen>;
}

function Fotosintesis() {
  const { foto, fotosintesis, lang } = useSains();
  const [quiz, setQuiz] = useState(false);
  const [done, setDone] = useState(false);
  const [round, setRound] = useState(0);
  const [flat, setFlat] = useState(() => !webglOk());
  const items = useMemo<QuizItem[]>(() => fotosintesis(QUIZ_ROUNDS).map(({ prompt, options, answer }) => ({ prompt, options, answer })), [quiz, lang]);
  const lines = useMemo(() => fotoScript(lang), [lang]);

  if (quiz) {
    return (
      <>
        <TopBar back="/belajar/permainan" title={foto.name} subtitle={t("Kuiz")} />
        <MiniQuiz items={items} details={FOTO_DETAILS} onExit={() => { setQuiz(false); setDone(false); setRound((r) => r + 1); }} />
      </>
    );
  }
  return (
    <>
      <TopBar back="/belajar/permainan" title={foto.name} subtitle={t("Beri pokok air, CO₂ dan cahaya matahari. Lihat ia membesar.")} />
      <div className="mx-auto max-w-md space-y-3">
        {flat ? (
          <Card className="text-center text-sm text-slate-600">{t("Peranti ini tidak menyokong 3D. Baca penerangan dan cuba kuiz.")}</Card>
        ) : (
          <Suspense fallback={<div className="grid aspect-[4/5] w-full place-items-center rounded-3xl bg-sky-200 text-sm font-bold text-slate-600">3D…</div>}>
            <Fotosintesis3D key={round} foto={foto} onDone={() => setDone(true)} onFail={() => setFlat(true)} />
          </Suspense>
        )}

        <div className="flex flex-wrap items-center justify-center gap-2">
          {!flat && <button type="button" onClick={() => { setDone(false); setRound((r) => r + 1); }} className={GHOST}>{t("Main semula")}</button>}
          <button type="button" onClick={() => setQuiz(true)}
            className={`rounded-full px-5 py-2.5 text-sm font-extrabold text-white shadow-md ${done ? "animate-pulse bg-emerald-600" : "bg-emerald-500"}`}>
            {done ? t("Mula kuiz") : t("Kuiz")}
          </button>
        </div>

        <Card className="space-y-2">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-black leading-tight text-slate-900">{foto.name}</h2>
              <p className="text-sm font-bold text-emerald-700">{foto.tagline}.</p>
            </div>
            <ListenButton lines={lines} />
          </div>
          <p className="text-sm leading-relaxed text-slate-700">{foto.detail}</p>
          <ul className="list-disc space-y-1 pl-5 text-sm leading-relaxed text-slate-700">
            {foto.facts.map((f) => <li key={f}>{f}</li>)}
          </ul>
          <p className="rounded-2xl bg-emerald-50 p-3 text-center text-base font-extrabold leading-relaxed text-emerald-900" aria-label={`${foto.co2} + ${foto.water} + ${foto.light} → ${foto.glucose} + ${foto.oxygen}`}>
            6 CO₂ + 6 H₂O + {foto.light.toLowerCase()} → C₆H₁₂O₆ + 6 O₂
          </p>
        </Card>
      </div>
    </>
  );
}
